import React from 'react';
import { cn } from 'src/lib/utils';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'src/components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from 'src/components/ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
} from 'src/components/ui/dropdown-menu';
import { ChevronUp, ChevronDown } from 'lucide-react';

// Column definition type
const ColumnDefinition = {
  key: String,
  header: String,
  width: String,
  sticky: Boolean,
  stickyPosition: String, // 'left' | 'right'
  sortable: Boolean,
  render: Function,
  className: String,
  align: String, // 'left' | 'center' | 'right'
  tooltip: Boolean,
  minWidth: String,
};

const DataTable = ({
  data = [],
  columns = [],
  sortable = false,
  onSort,
  sortDirection = 'asc',
  sortColumn = null,
  className = '',
  rowClassName,
  headerClassName,
  cellClassName,
  hover = true,
  stickyHeader = false,
  loading = false,
  emptyState,
  headerWhenEmpty = false,
  onRowClick,
  // Expandable rows
  expandable = false,
  expandedRowIds = [],
  getRowId,
  expandedRowRender,
  // Pagination props
  pagination = false,
  currentPage = 1,
  totalPages = 1,
  pageSize = 10,
  totalItems = 0,
  onPageChange,
  onPageSizeChange,
  // Layout
  tableLayout = 'fixed', // 'fixed' | 'auto'
  ...props
}) => {
  // Handle sort click
  const handleSort = (columnKey) => {
    if (!onSort || !sortable) return;
    onSort(columnKey);
  };

  // Get sticky positioning styles
  const getStickyStyles = (column, index) => {
    if (!column.sticky) return {};

    const baseStyles = {
      position: 'sticky',
      backgroundColor: 'hsl(var(--card))',
    };

    if (column.stickyPosition === 'left') {
      let leftPosition = 0;
      // Calculate left position based on previous sticky columns
      for (let i = 0; i < index; i++) {
        if (columns[i].sticky && columns[i].stickyPosition === 'left') {
          leftPosition += parseInt(columns[i].width) || 120;
        }
      }
      return {
        ...baseStyles,
        left: `${leftPosition}px`,
        boxShadow: 'inset -8px 0 8px -8px rgba(2,6,23,0.08)',
      };
    }

    if (column.stickyPosition === 'right') {
      let rightPosition = 0;
      // Calculate right position based on following sticky columns
      for (let i = columns.length - 1; i > index; i--) {
        if (columns[i].sticky && columns[i].stickyPosition === 'right') {
          rightPosition += parseInt(columns[i].width) || 120;
        }
      }
      return {
        ...baseStyles,
        right: `${rightPosition}px`,
        boxShadow: 'inset 8px 0 8px -8px rgba(2,6,23,0.08)',
      };
    }

    return baseStyles;
  };

  // Tooltip that only appears when content overflows available width
  const OverflowTooltip = ({ text }) => {
    const textRef = React.useRef(null);
    const [isOverflowing, setIsOverflowing] = React.useState(false);

    const measureOverflow = React.useCallback(() => {
      const el = textRef.current;
      if (!el) return;
      const overflowing = el.scrollWidth > el.clientWidth;
      if (overflowing !== isOverflowing) {
        setIsOverflowing(overflowing);
      }
    }, [isOverflowing]);

    React.useEffect(() => {
      // Measure after mount and whenever text changes
      const id = requestAnimationFrame(measureOverflow);
      return () => cancelAnimationFrame(id);
    }, [text, measureOverflow]);

    React.useEffect(() => {
      const el = textRef.current;
      if (!el) return;
      let rafId;
      const ro = new ResizeObserver(() => {
        cancelAnimationFrame(rafId);
        rafId = requestAnimationFrame(measureOverflow);
      });
      ro.observe(el);
      const onWindowResize = () => {
        cancelAnimationFrame(rafId);
        rafId = requestAnimationFrame(measureOverflow);
      };
      window.addEventListener('resize', onWindowResize);
      return () => {
        ro.disconnect();
        window.removeEventListener('resize', onWindowResize);
        cancelAnimationFrame(rafId);
      };
    }, [measureOverflow]);

    const content = (
      <div ref={textRef} className={cn('block w-full min-w-0 truncate')}>
        {text}
      </div>
    );

    if (!isOverflowing) {
      return content;
    }

    return (
      <TooltipProvider>
        <Tooltip delayDuration={0}>
          <TooltipTrigger asChild>{content}</TooltipTrigger>
          <TooltipContent
            side="top"
            align="start"
            className="max-w-xs text-black dark:text-foreground-strong text-xs p-2 z-50"
          >
            <p>{text}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  };

  // Render cell content
  const renderCell = (column, value, row, rowIndex) => {
    const cellContent = column.render
      ? column.render(value, row, rowIndex)
      : value;

    if (column.tooltip && typeof cellContent === 'string') {
      return <OverflowTooltip text={cellContent} />;
    }

    return cellContent;
  };

  // Loading state
  if (loading) {
    return (
      <div className={cn('bg-card rounded-xl border border-border overflow-hidden', className)}>
        <div className="overflow-x-auto">
          <Table className="table-fixed">
            <TableHeader>
              <TableRow>
                {columns.map((column, index) => (
                  <TableHead
                    key={column.key}
                    className={cn(
                      'text-[11px] font-semibold uppercase tracking-[0.06em] text-muted-foreground py-[13px] px-4 bg-card whitespace-nowrap border-b border-border',
                      column.className,
                      headerClassName
                    )}
                    style={{
                      width: column.width,
                      minWidth: column.minWidth,
                      textAlign: column.align || 'left',
                      ...getStickyStyles(column, index),
                    }}
                  >
                    {column.header}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {Array.from({ length: 5 }).map((_, index) => (
                <TableRow key={index} className="animate-pulse">
                  {columns.map((column, colIndex) => (
                    <TableCell
                      key={column.key}
                      className={cn(
                        'py-[14px] px-4',
                        column.className,
                        cellClassName?.(column.key, null, index)
                      )}
                      style={{
                        width: column.width,
                        minWidth: column.minWidth,
                        textAlign: column.align || 'left',
                        ...getStickyStyles(column, colIndex),
                      }}
                    >
                      <div className="h-4 bg-muted rounded" />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    );
  }


  // Empty state
  if (data.length === 0) {
    if (headerWhenEmpty) {
      return (
        <div className={cn('bg-card rounded-xl border border-border overflow-hidden', className)}>
          <div className="overflow-x-auto">
            <Table className={tableLayout === 'fixed' ? 'table-fixed' : 'table-auto'}>
              <TableHeader className={stickyHeader ? 'sticky top-0 bg-card' : ''}>
                <TableRow>
                  {columns.map((column, index) => (
                    <TableHead
                      key={column.key}
                      className={cn('text-[11px] font-semibold uppercase tracking-[0.06em] text-muted-foreground py-[13px] px-4 bg-card whitespace-nowrap border-b border-border', column.className, headerClassName)}
                      style={{
                        width: column.width,
                        minWidth: column.minWidth,
                        textAlign: column.align || 'left',
                        ...getStickyStyles(column, index),
                      }}
                    >
                      {typeof column.header === 'function' ? column.header() : column.header}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
            </Table>
          </div>
          {emptyState ?? (
            <div className="p-8 text-center text-muted-foreground">
              No data available
            </div>
          )}
        </div>
      );
    }
    if (emptyState) {
      return emptyState;
    }
    return (
      <div className={cn('bg-card rounded-xl border border-border overflow-hidden', className)}>
        <div className="p-8 text-center text-muted-foreground">
          No data available
        </div>
      </div>
    );
  }

  return (
    <div className={cn('bg-card rounded-xl border border-border overflow-hidden', className)}>
      <div className="overflow-x-auto">
        <Table
          className={tableLayout === 'fixed' ? 'table-fixed' : 'table-auto'}
        >
          {/* Collapsible row with smooth height transition */}
          {(() => {
            const CollapsibleRow = ({ open, row, rowIndex }) => {
              if (typeof expandedRowRender !== 'function') return null;
              const contentRef = React.useRef(null);
              const [shouldRender, setShouldRender] = React.useState(open);

              React.useEffect(() => {
                if (open) {
                  setShouldRender(true);
                } else if (contentRef.current) {
                  // Trigger close animation then unmount after transition
                  const el = contentRef.current;
                  el.style.maxHeight = el.scrollHeight + 'px';
                  // ensure the above style is applied before collapsing
                  requestAnimationFrame(() => {
                    el.style.maxHeight = '0px';
                    el.style.opacity = '0.6';
                  });
                }
              }, [open]);

              React.useEffect(() => {
                if (!shouldRender) return;
                const el = contentRef.current;
                if (!el) return;
                if (open) {
                  // Start from 0 and expand to content height
                  el.style.maxHeight = '0px';
                  el.style.opacity = '0.6';
                  requestAnimationFrame(() => {
                    el.style.maxHeight = el.scrollHeight + 'px';
                    el.style.opacity = '1';
                  });
                }
              }, [shouldRender, open]);

              const handleTransitionEnd = (e) => {
                if (e.propertyName !== 'max-height') return;
                const el = contentRef.current;
                if (!el) return;
                if (open) {
                  // Allow natural height after opening completes
                  el.style.maxHeight = 'none';
                } else {
                  setShouldRender(false);
                }
              };

              if (!shouldRender && !open) return null;

              return (
                <TableRow aria-hidden={!open}>
                  <TableCell colSpan={columns.length} className="p-0">
                    <div
                      ref={contentRef}
                      onTransitionEnd={handleTransitionEnd}
                      style={{
                        overflow: 'hidden',
                        maxHeight: open ? 'none' : '0px',
                        transition: 'max-height 300ms ease, opacity 200ms ease',
                        opacity: open ? 1 : 0.6,
                        willChange: 'max-height, opacity',
                      }}
                    >
                      {expandedRowRender(row, rowIndex)}
                    </div>
                  </TableCell>
                </TableRow>
              );
            };

            // expose for JSX below via closure
            return null;
          })()}
          <TableHeader
            className={stickyHeader ? 'sticky top-0 bg-card' : ''}
          >
            <TableRow>
              {columns.map((column, index) => (
                <TableHead
                  key={column.key}
                  className={cn(
                    'text-[11px] font-semibold uppercase tracking-[0.06em] text-muted-foreground py-[13px] px-4 bg-card whitespace-nowrap border-b border-border',
                    column.className,
                    headerClassName
                  )}
                  style={{
                    width: column.width,
                    minWidth: column.minWidth,
                    textAlign: column.align || 'left',
                    ...getStickyStyles(column, index),
                  }}
                >
                  {column.sortable && sortable ? (
                    <button
                      type="button"
                      className={cn(
                        'flex items-center gap-1 cursor-pointer hover:text-primary transition-colors w-full',
                        column.align === 'center' &&
                          'justify-center text-center'
                      )}
                      onClick={() => handleSort(column.key)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleSort(column.key);
                        }
                      }}
                      aria-label={`Sort by ${typeof column.header === 'function' ? column.key : column.header} ${sortDirection === 'desc' ? 'newest first' : 'oldest first'}`}
                    >
                      <span>
                        {typeof column.header === 'function'
                          ? column.header()
                          : column.header}
                      </span>
                      {sortColumn === column.key && (
                        <ChevronUp
                          className={cn(
                            'h-3 w-3 transition-transform duration-200',
                            sortDirection === 'desc'
                              ? 'text-foreground-strong rotate-180'
                              : 'text-foreground-strong'
                          )}
                        />
                      )}
                    </button>
                  ) : typeof column.header === 'function' ? (
                    column.header()
                  ) : (
                    column.header
                  )}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.map((row, rowIndex) => {
              const derivedRowId = getRowId
                ? getRowId(row, rowIndex)
                : row.id || rowIndex;
              const isExpanded =
                expandable && expandedRowIds?.includes(derivedRowId);

              return (
                <React.Fragment key={derivedRowId}>
                  <TableRow
                    className={cn(
                      hover && 'hover:bg-brand-bg-subtle group',
                      rowClassName?.(row, rowIndex),
                      onRowClick && 'cursor-pointer'
                    )}
                    onClick={(e) => {
                      const target = e.target;
                      if (
                        target &&
                        typeof target.closest === 'function' &&
                        target.closest('[data-prevent-row-click="true"]')
                      ) {
                        return;
                      }
                      onRowClick?.(row, rowIndex, e);
                    }}
                  >
                    {columns.map((column, colIndex) => (
                      <TableCell
                        key={column.key}
                        className={cn(
                          'text-sm py-[14px] px-4',
                          column.className,
                          cellClassName?.(column.key, row, rowIndex)
                        )}
                        style={{
                          width: column.width,
                          minWidth: column.minWidth,
                          textAlign: column.align || 'left',
                          ...getStickyStyles(column, colIndex),
                        }}
                      >
                        {renderCell(column, row[column.key], row, rowIndex)}
                      </TableCell>
                    ))}
                  </TableRow>

                  {expandable &&
                    // eslint-disable-next-line react/no-unstable-nested-components
                    (({ open, r, idx }) => {
                      // Inline lightweight wrapper to use CollapsibleRow defined above
                      const CollapsibleRow = ({
                        open: o,
                        row: rr,
                        rowIndex: ii,
                      }) => {
                        if (typeof expandedRowRender !== 'function')
                          return null;
                        const contentRef = React.useRef(null);
                        const [shouldRender, setShouldRender] =
                          React.useState(o);

                        React.useEffect(() => {
                          if (o) {
                            setShouldRender(true);
                          } else if (contentRef.current) {
                            const el = contentRef.current;
                            el.style.maxHeight = el.scrollHeight + 'px';
                            requestAnimationFrame(() => {
                              el.style.maxHeight = '0px';
                              el.style.opacity = '0.6';
                            });
                          }
                        }, [o]);

                        React.useEffect(() => {
                          if (!shouldRender) return;
                          const el = contentRef.current;
                          if (!el) return;
                          if (o) {
                            el.style.maxHeight = '0px';
                            el.style.opacity = '0.6';
                            requestAnimationFrame(() => {
                              el.style.maxHeight = el.scrollHeight + 'px';
                              el.style.opacity = '1';
                            });
                          }
                        }, [shouldRender, o]);

                        const handleTransitionEnd = (e) => {
                          if (e.propertyName !== 'max-height') return;
                          const el = contentRef.current;
                          if (!el) return;
                          if (o) {
                            el.style.maxHeight = 'none';
                          } else {
                            setShouldRender(false);
                          }
                        };

                        if (!shouldRender && !o) return null;

                        return (
                          <TableRow aria-hidden={!o}>
                            <TableCell colSpan={columns.length} className="p-0">
                              <div
                                ref={contentRef}
                                onTransitionEnd={handleTransitionEnd}
                                style={{
                                  overflow: 'hidden',
                                  maxHeight: o ? 'none' : '0px',
                                  transition:
                                    'max-height 300ms ease, opacity 200ms ease',
                                  opacity: o ? 1 : 0.6,
                                  willChange: 'max-height, opacity',
                                }}
                              >
                                {expandedRowRender(rr, ii)}
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      };

                      return (
                        <CollapsibleRow open={open} row={r} rowIndex={idx} />
                      );
                    })({ open: isExpanded, r: row, idx: rowIndex })}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {pagination && (
        <div className="flex items-center justify-between p-2 px-4 border-t border-border">
          {/* Rows per page selector */}
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground text-[11px]">
                Rows per page
              </span>
              <div className="relative">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      type="button"
                      aria-label="Rows per page selector"
                      className="bg-card border border-input rounded-[6px] px-2 py-1 pr-4 text-[12px] text-foreground-strong focus:outline-none focus:border-primary inline-flex items-center gap-1"
                    >
                      {pageSize}
                      <ChevronDown className="h-3 w-3 text-muted-foreground" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="start"
                    className="p-0 min-w-[4rem]"
                  >
                    <DropdownMenuRadioGroup
                      value={String(pageSize)}
                      onValueChange={(val) => onPageSizeChange?.(Number(val))}
                    >
                      {[5, 10, 25, 50].map((size) => (
                        <DropdownMenuRadioItem
                          key={size}
                          value={String(size)}
                          className="text-xs"
                        >
                          {size}
                        </DropdownMenuRadioItem>
                      ))}
                    </DropdownMenuRadioGroup>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>

            {/* Entry count display */}
            <div className="text-muted-foreground text-[11px]">
              showing{' '}
              <span className="font-bold">
                {(currentPage - 1) * pageSize + 1}
              </span>{' '}
              to{' '}
              <span className="font-bold">
                {Math.min(currentPage * pageSize, totalItems)}
              </span>{' '}
              of <span className="font-bold">{totalItems}</span> entries
            </div>
          </div>

          {/* Pagination navigation controls */}
          <div className="flex items-center gap-2">
            <button
              type="button"
              style={{
                fontSize: '10px',
                padding: '0.25rem 0.5rem',
              }}
              onClick={() => onPageChange?.(currentPage - 1)}
              disabled={currentPage === 1}
              className={`px-3 py-1.5 rounded-sm border text-xs transition-colors ${
                currentPage === 1
                  ? 'bg-muted border-border text-foreground-muted cursor-not-allowed'
                  : 'bg-card border-input text-muted-foreground hover:bg-muted'
              }`}
            >
              Previous
            </button>

            <div className="flex items-center gap-1">
              <input
                type="number"
                min="1"
                max={totalPages}
                value={currentPage}
                onChange={(e) => {
                  const newPage = parseInt(e.target.value, 10);
                  if (newPage >= 1 && newPage <= totalPages) {
                    onPageChange?.(newPage);
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.target.blur();
                  }
                }}
                className="w-12 p-1 text-center border border-input rounded-[6px] bg-card text-foreground-strong font-medium text-[12px] focus:outline-none focus:border-primary"
              />
              <span className="text-muted-foreground text-xs font-semibold">
                of {totalPages}
              </span>
            </div>

            <button
              type="button"
              onClick={() => onPageChange?.(currentPage + 1)}
              style={{
                fontSize: '10px',
                padding: '0.25rem 0.5rem',
              }}
              disabled={currentPage >= totalPages}
              className={`px-3 py-1.5 rounded-sm border text-xs transition-colors ${
                currentPage >= totalPages
                  ? 'bg-muted border-border text-foreground-muted cursor-not-allowed'
                  : 'bg-card border-input text-muted-foreground hover:bg-muted'
              }`}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DataTable;
